/**
 * Route the editor's undo / redo (top-bar buttons, the ⋯ menu, Ctrl/Cmd+Z on the editor page) to
 * the active slide's OWN history when the slide keeps one — content-v2 does, in its canvas iframe.
 *
 * The presenter's history restores whole-slide snapshots. For a slide whose content is edited
 * inside a plugin iframe, that is a coarse, out-of-step undo: one press can revert several edits
 * the plugin saved together, and the plugin's own undo stack no longer matches the slide. So while
 * such a slide is open and it has something to undo, the press goes to the plugin instead; when it
 * has nothing (e.g. right after the slide was added), the presenter's history runs as before.
 *
 * Opt-in: only a plugin that registers here is routed (SlidePluginIframeEmbed, when the plugin
 * passes an `onUndoRedo` callback). Every other slide type keeps the presenter's history.
 *
 * Order: while the slide is open its own edits are undone first, then the presenter's history
 * (content-v2 edits are saved as a slide attribute, which the presenter's snapshots don't track,
 * so the two never undo the same change). A control that undoes one specific presenter action —
 * the "N slides deleted" toast — passes `presenterOnly` to undoSlidesAction.
 *
 * One slot: the on-screen editable instance registers; preload and presenting instances never do.
 */
import Vue from 'vue'

// Reactive so the history getters (and the buttons' disabled state) follow the plugin's state.
const state = Vue.observable({ target: null })

/**
 * @param {string} slideId the slide the handler edits
 * @param {(action: 'undo' | 'redo') => void} run
 */
export function registerSlideUndo(slideId, run) {
  state.target = {
    slideId: String(slideId), run, canUndo: false, canRedo: false,
  }
  return state.target
}

/** Clears the registration — a no-op unless `run` is the registered handler. */
export function unregisterSlideUndo(run) {
  if (state.target && state.target.run === run) state.target = null
}

/** The plugin reports whether its history can go back / forward. */
export function setSlideUndoState(run, { canUndo, canRedo } = {}) {
  if (!state.target || state.target.run !== run) return
  state.target = { ...state.target, canUndo: Boolean(canUndo), canRedo: Boolean(canRedo) }
}

/**
 * The plugin handler for `action` on `slideId`, when it is registered for that slide and has
 * something to do; otherwise null (the caller uses the presenter's history).
 */
export function slideUndoTarget(slideId, action) {
  const t = state.target
  if (!t || slideId === undefined || slideId === null || t.slideId !== String(slideId)) return null
  const can = action === 'undo' ? t.canUndo : t.canRedo
  return can ? t : null
}
